// src/components/staff/PayrollSummary.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import { tenantDataService } from '../../services/tenantDataService';
import { 
  FiDollarSign, FiCalendar, FiClock, FiTrendingUp, 
  FiFileText, FiDownload, FiInfo, FiEdit3, FiSave, FiX 
} from 'react-icons/fi';
import toast from 'react-hot-toast';

// Helper function to calculate earnings from compensation plan
const calculateEarningsFromPlan = (appointments, compensationPlan, clinicSettings) => {
  if (!compensationPlan || !appointments.length) return 0;

  let totalEarnings = 0;

  appointments.forEach(appointment => {
    const treatmentType = appointment.type || 'General';
    
    // Calculate based on compensation plan structure
    if (compensationPlan.commissionBasis === 'percentage') {
      // Need to get invoice amount for percentage calculation
      // For now, estimate based on treatment rates
      const treatmentRate = clinicSettings?.treatmentRates?.[treatmentType];
      if (treatmentRate) {
        const baseAmount = treatmentRate.value;
        const percentage = compensationPlan.commissionRate || 0;
        totalEarnings += baseAmount * (percentage / 100);
      }
    } else if (compensationPlan.commissionBasis === 'flat') {
      totalEarnings += compensationPlan.commissionRate || 0;
    } else if (compensationPlan.commissionBasis === 'treatment') {
      // Use treatment-specific rates from clinic settings
      const treatmentRate = clinicSettings?.treatmentRates?.[treatmentType];
      if (treatmentRate) {
        totalEarnings += treatmentRate.value;
      }
    }
  });

  return totalEarnings;
};

const PayrollSummary = () => {
  const { userProfile } = useAuth();
  const { currentTenant } = useTenant();
  const [payrollData, setPayrollData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customTaxRate, setCustomTaxRate] = useState(25);
  const [isEditingTaxRate, setIsEditingTaxRate] = useState(false);
  const [userCompensationPlan, setUserCompensationPlan] = useState(null);
  const [clinicSettings, setClinicSettings] = useState(null);

  useEffect(() => {
    loadPayrollData();
    loadUserTaxRate();
  }, [userProfile?.uid, currentTenant?.id]);

  const loadUserTaxRate = async () => {
    if (!userProfile?.uid || !currentTenant?.id) return;
    
    try {
      tenantDataService.setTenantContext(currentTenant.id);
      const userSettings = await tenantDataService.getById('userSettings', userProfile.uid);
      if (userSettings?.taxRate) {
        setCustomTaxRate(userSettings.taxRate);
      }
    } catch (error) {
      console.log('No custom tax rate found, using default 25%');
    }
  };

  const saveTaxRate = async () => {
    if (!userProfile?.uid || !currentTenant?.id) return;
    
    try {
      tenantDataService.setTenantContext(currentTenant.id);
      
      // Try to update first, if it fails, create new document
      try {
        await tenantDataService.update('userSettings', userProfile.uid, {
          taxRate: customTaxRate,
          updatedAt: new Date().toISOString()
        });
      } catch (updateError) {
        // Document doesn't exist, create it
        await tenantDataService.create('userSettings', {
          id: userProfile.uid,
          taxRate: customTaxRate,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }
      
      setIsEditingTaxRate(false);
      loadPayrollData(); // Recalculate with new tax rate
      toast.success('Tax rate updated successfully');
    } catch (error) {
      console.error('Error saving tax rate:', error);
      toast.error('Failed to save tax rate');
    }
  };

  const loadPayrollData = async () => {
    if (!userProfile?.uid || !currentTenant?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      tenantDataService.setTenantContext(currentTenant.id);

      // Load appointments and compensation plans
      const [appointments, compensationPlans] = await Promise.all([
        tenantDataService.getAll('appointments'),
        tenantDataService.getAll('compensationPlans')
      ]);

      console.log('💰 PayrollSummary: Loaded appointments:', appointments.length);
      console.log('💰 PayrollSummary: Loaded compensation plans:', compensationPlans.length);
      console.log('💰 PayrollSummary: Current user ID:', userProfile.uid);

      const staffAppointments = appointments.filter(apt => {
        const isStaffMatch = apt.staffId === userProfile.uid;
        return isStaffMatch;
      });

      console.log('💰 PayrollSummary: Filtered staff appointments:', staffAppointments.length);

      // Find user's compensation plan
      const foundUserCompensationPlan = compensationPlans.find(plan => 
        plan.staffId === userProfile.uid
      );
      setUserCompensationPlan(foundUserCompensationPlan);

      console.log('💰 PayrollSummary: User compensation plan:', foundUserCompensationPlan);

      // Calculate current pay period (monthly)
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      const startOfYear = new Date(currentYear, 0, 1);
      
      // Current pay period (full calendar month)
      const payPeriodStart = new Date(currentYear, currentMonth, 1);
      const payPeriodEnd = new Date(currentYear, currentMonth + 1, 0); // Last day of current month
      // Current pay period appointments (full month)
      const currentPeriodAppointments = staffAppointments.filter(apt => {
        const aptDate = new Date(apt.date);
        return aptDate >= payPeriodStart && aptDate <= payPeriodEnd && (apt.status === 'completed' || apt.status === 'attended');
      });

      // Year to date appointments
      const ytdAppointments = staffAppointments.filter(apt => {
        const aptDate = new Date(apt.date);
        return aptDate >= startOfYear && aptDate <= now && (apt.status === 'completed' || apt.status === 'attended');
      });

      // Load clinic settings to determine compensation structure
      const loadedClinicSettings = await tenantDataService.getById('settings', 'clinic');
      setClinicSettings(loadedClinicSettings);
      const isAdvancedPlansEnabled = loadedClinicSettings?.compensation?.enableAdvancedPlans || false;
      
      console.log('💰 PayrollSummary: Advanced plans enabled:', isAdvancedPlansEnabled);
      
      // Calculate earnings based on clinic's compensation structure
      let currentPeriodEarnings = 0;
      let ytdEarnings = 0;
      
      if (isAdvancedPlansEnabled && foundUserCompensationPlan) {
        // Use compensation plan rates
        console.log('💰 PayrollSummary: Using compensation plan:', foundUserCompensationPlan.name);
        currentPeriodEarnings = calculateEarningsFromPlan(currentPeriodAppointments, foundUserCompensationPlan, loadedClinicSettings);
        ytdEarnings = calculateEarningsFromPlan(ytdAppointments, foundUserCompensationPlan, loadedClinicSettings);
      } else {
        // Use treatment rates with role-based percentage
        const rolePercentage = loadedClinicSettings?.roleRates?.[userProfile.role] || 65; // Default percentage
        console.log('💰 PayrollSummary: Using role percentage for', userProfile.role, ':', rolePercentage + '%');
        
        // Calculate earnings based on actual treatment rates
        currentPeriodEarnings = currentPeriodAppointments.reduce((total, apt) => {
          const treatmentType = apt.type || 'Physiotherapy';
          
          // Try both treatmentRates and treatmentTypes formats (same logic as payrollService)
          let treatmentRate = loadedClinicSettings?.treatmentRates?.[treatmentType];
          
          if (!treatmentRate && loadedClinicSettings?.treatmentTypes) {
            const treatmentTypeObj = loadedClinicSettings.treatmentTypes.find(t => t.name === treatmentType);
            if (treatmentTypeObj) {
              treatmentRate = {
                type: treatmentTypeObj.pricingType,
                value: treatmentTypeObj.value
              };
            }
          }
          
          if (treatmentRate) {
            let appointmentValue = 0;
            if (treatmentRate.type === 'hourly') {
              appointmentValue = (apt.duration / 60) * treatmentRate.value;
            } else {
              appointmentValue = treatmentRate.value;
            }
            return total + (appointmentValue * (rolePercentage / 100));
          }
          
          return total;
        }, 0);
        
        ytdEarnings = ytdAppointments.reduce((total, apt) => {
          const treatmentType = apt.type || 'Physiotherapy';
          
          // Try both treatmentRates and treatmentTypes formats
          let treatmentRate = loadedClinicSettings?.treatmentRates?.[treatmentType];
          
          if (!treatmentRate && loadedClinicSettings?.treatmentTypes) {
            const treatmentTypeObj = loadedClinicSettings.treatmentTypes.find(t => t.name === treatmentType);
            if (treatmentTypeObj) {
              treatmentRate = {
                type: treatmentTypeObj.pricingType,
                value: treatmentTypeObj.value
              };
            }
          }
          
          if (treatmentRate) {
            let appointmentValue = 0;
            if (treatmentRate.type === 'hourly') {
              appointmentValue = (apt.duration / 60) * treatmentRate.value;
            } else {
              appointmentValue = treatmentRate.value;
            }
            return total + (appointmentValue * (rolePercentage / 100));
          }
          
          return total;
        }, 0);
      }
      
      const currentPeriodHours = currentPeriodAppointments.length; // 1 hour per appointment
      const ytdHours = ytdAppointments.length;
      const effectiveHourlyRate = currentPeriodHours > 0 ? currentPeriodEarnings / currentPeriodHours : (loadedClinicSettings?.roleRates?.[userProfile.role] || 50);

      // Calculate tax estimates using custom tax rate
      const taxRate = customTaxRate / 100; // Convert percentage to decimal
      const currentPeriodTax = currentPeriodEarnings * taxRate;
      const ytdTax = ytdEarnings * taxRate;

      setPayrollData({
        currentPeriod: {
          hours: currentPeriodHours,
          grossPay: currentPeriodEarnings,
          estimatedTax: currentPeriodTax,
          netPay: currentPeriodEarnings - currentPeriodTax,
          appointments: currentPeriodAppointments.length,
          startDate: payPeriodStart,
          endDate: payPeriodEnd
        },
        yearToDate: {
          hours: ytdHours,
          grossPay: ytdEarnings,
          estimatedTax: ytdTax,
          netPay: ytdEarnings - ytdTax,
          appointments: ytdAppointments.length
        },
        hourlyRate: effectiveHourlyRate,
        payFrequency: 'Monthly',
        taxRate: customTaxRate
      });

    } catch (error) {
      console.error('Error loading payroll data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {[1, 2].map(i => (
            <div key={i} className="bg-white rounded-lg border p-6">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const formatCurrency = (amount) => `$${amount?.toFixed(2) || '0.00'}`;
  const formatDate = (date) => date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Financial Summary</h2>
        <div className="text-sm text-gray-600">
          Pay Frequency: {payrollData?.payFrequency}
        </div>
      </div>

      {/* Current Pay Period */}
      <div className="bg-gradient-to-r from-green-500 to-blue-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Current Pay Period</h3>
            <p className="text-green-100">
              {formatDate(payrollData?.currentPeriod?.startDate)} - {formatDate(payrollData?.currentPeriod?.endDate)}
            </p>
          </div>
          <FiDollarSign className="w-8 h-8 text-green-100" />
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-green-100 text-sm">Hours Worked</p>
            <p className="text-2xl font-bold">{payrollData?.currentPeriod?.hours || 0}</p>
          </div>
          <div>
            <p className="text-green-100 text-sm">Gross Pay</p>
            <p className="text-2xl font-bold">{formatCurrency(payrollData?.currentPeriod?.grossPay)}</p>
          </div>
          <div>
            <p className="text-green-100 text-sm">Est. Tax</p>
            <p className="text-2xl font-bold">{formatCurrency(payrollData?.currentPeriod?.estimatedTax)}</p>
          </div>
          <div>
            <p className="text-green-100 text-sm">Net Pay</p>
            <p className="text-2xl font-bold">{formatCurrency(payrollData?.currentPeriod?.netPay)}</p>
          </div>
        </div>
      </div>

      {/* Financial Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Compensation Structure */}
        <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 font-medium text-sm">Compensation</p>
              <p className="text-lg font-bold text-blue-900">
                {userCompensationPlan ? userCompensationPlan.name : `${userProfile.role} Role`}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                {userCompensationPlan ? 
                  `${userCompensationPlan.commissionBasis} based` : 
                  `${clinicSettings?.roleRates?.[userProfile.role] || 50}% of treatment rates`
                }
              </p>
            </div>
            <FiDollarSign className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        {/* Appointments This Period */}
        <div className="bg-purple-50 rounded-lg p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 font-medium text-sm">Appointments</p>
              <p className="text-2xl font-bold text-purple-900">
                {payrollData?.currentPeriod?.appointments || 0}
              </p>
            </div>
            <FiCalendar className="w-8 h-8 text-purple-500" />
          </div>
        </div>

        {/* Average per Appointment */}
        <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-600 font-medium text-sm">Avg per Appointment</p>
              <p className="text-2xl font-bold text-green-900">
                {payrollData?.currentPeriod?.appointments > 0 
                  ? formatCurrency(payrollData.currentPeriod.grossPay / payrollData.currentPeriod.appointments)
                  : '$0.00'
                }
              </p>
            </div>
            <FiTrendingUp className="w-8 h-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* Year to Date Summary */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Year to Date Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-900">
              {payrollData?.yearToDate?.hours || 0}
            </p>
            <p className="text-sm text-gray-600">Total Hours</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">
              {formatCurrency(payrollData?.yearToDate?.grossPay)}
            </p>
            <p className="text-sm text-gray-600">Gross Earnings</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-red-600">
              {formatCurrency(payrollData?.yearToDate?.estimatedTax)}
            </p>
            <p className="text-sm text-gray-600">Est. Taxes</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600">
              {formatCurrency(payrollData?.yearToDate?.netPay)}
            </p>
            <p className="text-sm text-gray-600">Net Earnings</p>
          </div>
        </div>
      </div>

      {/* Pay History & Documents */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Pay Stubs */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Pay Stubs</h3>
            <FiFileText className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-3">
            <div className="text-center py-8">
              <FiFileText className="mx-auto w-12 h-12 text-gray-400 mb-4" />
              <p className="text-gray-600">Pay stub integration coming soon</p>
              <p className="text-sm text-gray-500 mt-2">
                Contact HR for historical pay information
              </p>
            </div>
          </div>
        </div>

        {/* Tax Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Tax Information</h3>
            <FiInfo className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <FiInfo className="w-5 h-5 text-yellow-500 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800">Tax Estimates</p>
                  <p className="text-sm text-yellow-600 mt-1">
                    Tax calculations are estimates only. Consult with a tax professional for accurate information.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Estimated Tax Rate:</span>
                <div className="flex items-center space-x-2">
                  {isEditingTaxRate ? (
                    <>
                      <input
                        type="number"
                        value={customTaxRate}
                        onChange={(e) => setCustomTaxRate(Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                        className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      <span className="text-sm">%</span>
                      <button
                        onClick={saveTaxRate}
                        className="p-1 text-green-600 hover:text-green-700"
                        title="Save tax rate"
                      >
                        <FiSave className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setIsEditingTaxRate(false)}
                        className="p-1 text-gray-600 hover:text-gray-700"
                        title="Cancel"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="font-medium">{customTaxRate}%</span>
                      <button
                        onClick={() => setIsEditingTaxRate(true)}
                        className="p-1 text-blue-600 hover:text-blue-700"
                        title="Edit tax rate"
                      >
                        <FiEdit3 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">YTD Tax Withheld:</span>
                <span className="font-medium">{formatCurrency(payrollData?.yearToDate?.estimatedTax)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollSummary;
